package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.api.AuthProviders
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.components.marketing.PublicTextLink
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

/**
 * Where Paddle's default payment link lands.
 *
 * Paddle requires one URL per account to be nominated as the "default payment link", and every
 * transaction it creates is that URL with `?_ptxn=<id>` appended. Without this page the account
 * cannot create a transaction at all — the API refuses with `transaction_default_checkout_url_not_set`
 * — so this is a prerequisite for the checkout button working, not a nicety.
 *
 * **It does not decide anything.** It opens Paddle's own checkout for a transaction the server
 * already created, and the plan changes only when the webhook says money moved. Nothing here is
 * trusted: a user who lands on this URL with a hand-typed `_ptxn` sees whatever that transaction is,
 * which is Paddle's business to authorize, not ours.
 *
 * The token it initializes with is the **client-side** token, which can open a checkout and nothing
 * else. The API key — which can read customers and cancel subscriptions — never reaches a browser.
 */
@Page("/checkout")
@Composable
fun CheckoutPage() {
    var failure by remember { mutableStateOf<String?>(null) }
    var started by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) { document.title = tr("checkout.checkout_lingvo", "Checkout — Lingvo") }

    val token = AuthProviders.paddleClientToken
    val environment = AuthProviders.paddleEnvironment

    // Paddle renders the form inside its own iframe with its own palette, and defaults to light.
    // On the ink ground that is black text on a dark surface — the payment form, unreadable, at the
    // one moment a customer is deciding whether to trust us with a card. Read once here rather than
    // inside the effect: `ColorMode.current` is composition state and the effect is not recomposed
    // by it, so a mode read in there would be whatever it was when the effect first ran.
    val dark = ColorMode.current.isDark

    // Whether the capability fetch has come back at all, however it came back.
    //
    // `AuthProviders` publishes `googleAvailable` in both arms of its `load()` — the answer on
    // success, `false` on failure — and it is the only field that does, so a non-null value here is
    // the one signal this page has that the round trip is over. Without it `token == null` conflates
    // "not landed yet" with "not configured", which is precisely what the comment below forbids and
    // what the notice further down used to do on every cold load.
    //
    // It is a proxy, and it reads as one. The durable version is a `resolved` flag on
    // `AuthProviders` set in both arms, which would also let this page separate "this deployment
    // takes no payments" from "we could not ask" — a distinction it currently has to leave to the
    // reader's Reload button.
    val providersResolved = AuthProviders.googleAvailable != null

    // Waits for the capability fetch rather than racing it. `AuthProviders` starts one request at
    // application start and shares the answer; null means it has not landed yet, which is a
    // different state from "not configured" and must not be rendered as an error.
    LaunchedEffect(token, environment) {
        if (started || token == null || environment == null) return@LaunchedEffect
        val transactionId = URLSearchParams(window.location.search).get("_ptxn")
        if (transactionId.isNullOrBlank()) {
            failure = tr(
                "checkout.this_link_is_missing_its_transaction",
                "This link is missing its transaction. Start the upgrade again from " +
                    "Settings → Billing → Compare plans.",
            )
            return@LaunchedEffect
        }
        started = true
        // Not `runCatching`. `openPaddleCheckout` suspends inside a `suspendCancellableCoroutine`
        // until Paddle's CDN answers, so leaving this page mid-flight cancels the effect's job and
        // throws `CancellationException` — which `runCatching` caught like any other failure and
        // turned into "Could not open the payment form" written into a composable on its way out,
        // while the parent job never learned it had been cancelled. Same defect the six sibling
        // pages already rethrow around.
        try {
            openPaddleCheckout(token, environment, transactionId, dark)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            failure = tr(
                "checkout.could_not_open_the_payment_form",
                "Could not open the payment form. Check your connection and try again.",
            )
        }
    }

    AuthPageScaffold(
        title = t("checkout.complete_your_subscription", "Complete your subscription"),
        subtitle = t(
            "checkout.paddle_handles_the_payment_we_never",
            "Paddle handles the payment. We never see your card details.",
        ),
        alternatePrompt = t("checkout.changed_your_mind", "Changed your mind?"),
        alternateAction = t("checkout.back_to_lingvo", "Back to Lingvo"),
        alternatePath = "/app",
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            failure?.let { message ->
                LAlert(
                    t("checkout.checkout_could_not_open", "Checkout could not open"),
                    message,
                    variant = LAlertVariant.DANGER,
                    // It arrives after paint, on the screen where somebody is about to pay.
                    live = true,
                )
            }
            if (failure == null && token == null) {
                if (!providersResolved) {
                    // The wait, drawn as the wait. This branch used to render "Payments are not
                    // available on this deployment" for the length of the `/auth/providers` round
                    // trip, so every customer arriving on a paid transaction was told their vendor
                    // takes no payments at the exact moment they were deciding whether to hand over
                    // a card. It is the same height as the frame that replaces it, so nothing moves.
                    LSkeleton(
                        Modifier.fillMaxWidth(),
                        height = PADDLE_FRAME_HEIGHT.px,
                        label = t("checkout.opening_the_payment_form", "Opening the payment form"),
                    )
                } else {
                    LAlert(
                        t("checkout.payments_arent_available_right_now", "Payments aren’t available right now"),
                        // Says only what is known. A deployment with no processor and a dropped
                        // `/auth/providers` request look identical from here — `AuthProviders`
                        // leaves the token null either way and never retries — so this claims
                        // neither, and offers the one recovery that actually restarts the fetch.
                        t(
                            "checkout.we_couldnt_confirm_a_payment_processor",
                            "We couldn’t confirm a payment processor for this deployment. Nothing " +
                                "has been charged. Reloading asks again; if it keeps happening, " +
                                "mail support@lingvo.dev and quote your transaction.",
                        ),
                        variant = LAlertVariant.WARNING,
                        live = true,
                    )
                    LButton(
                        label = t("checkout.reload_and_try_again", "Reload and try again"),
                        onClick = { window.location.reload() },
                        modifier = Modifier.fillMaxWidth(),
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            }
            // Paddle draws its inline frame into this element. It needs to exist in the DOM before
            // Paddle.Checkout.open runs, and it needs height of its own or the frame collapses.
            // Reserved only once there is something to reserve it for: while the answer is still
            // unknown the skeleton above holds the same height, and on a deployment that takes no
            // payments a permanently empty 480px box is dead space under a refusal.
            Div(
                Modifier
                    .fillMaxWidth()
                    .minHeight(if (failure == null && token != null) PADDLE_FRAME_HEIGHT.px else 0.px)
                    .toAttrs { classes("paddle-checkout-frame") },
            )
            // The three documents a payment page has to point at, on the one page in the product
            // that reaches a card and has no footer — `AuthPageScaffold` renders none, which is
            // right for a sign-in form and wrong here. `PublicFooter` states the reason these are
            // links rather than merely hosted pages: "a payment processor verifying a domain checks
            // that the site points at its own refund policy, and a human does the checking." A
            // customer deciding whether to enter a card should not have to leave the page to find
            // out what happens if they want the money back.
            Row(Modifier.fillMaxWidth().gap(Space.MD)) {
                PublicTextLink("/terms", t("checkout.terms", "Terms"), Modifier.fontSize(TypeScale.Small))
                PublicTextLink("/refunds", t("checkout.refunds", "Refunds"), Modifier.fontSize(TypeScale.Small))
                PublicTextLink("/privacy", t("checkout.privacy", "Privacy"), Modifier.fontSize(TypeScale.Small))
            }
        }
    }
}

/**
 * The inline frame's height: the `min-height` reserved for Paddle, and the height the skeleton holds
 * in the same place while the token is still in flight, so the two swap without moving the page.
 *
 * `frameInitialHeight` in the `js()` block below is the same number and has to stay the same number,
 * and it deliberately does **not** read this one — `js()` takes a compile-time constant string, and
 * a Kotlin binding named inside it has to survive minification to mean anything. Changing the frame
 * height means changing both.
 */
private const val PADDLE_FRAME_HEIGHT = 480

/**
 * Loads Paddle.js once and opens the checkout.
 *
 * Written against `js()` rather than an external declaration because the script is injected at
 * runtime: Paddle is not on the page until this function puts it there, so there is nothing for a
 * compile-time binding to bind to.
 */
/**
 * Opens the overlay, and says where to go once it is paid.
 *
 * `successUrl` belongs here and only here. The server used to pass the confirming page to Paddle as
 * `transaction.checkout.url`, which is the *payment* link rather than the page after it — so payers
 * were sent to a page with no Paddle.js, and no checkout ever opened. The redirect is a client-side
 * setting on the overlay; the payment link is a server-side field. They are different things and
 * conflating them cost every checkout this account has created.
 *
 * The confirming page owns the gap between paying and the webhook landing. Sending people straight
 * to billing is what makes them see "Free" seconds after paying, which is the moment they pay twice.
 */
private suspend fun openPaddleCheckout(
    token: String,
    environment: String,
    transactionId: String,
    dark: Boolean,
) {
    kotlinx.coroutines.suspendCancellableCoroutine { continuation ->
        val existing = document.querySelector("script[data-paddle]")
        val run = {
            js(
                """
                (function () {
                    if (environment === 'sandbox') { Paddle.Environment.set('sandbox'); }
                    Paddle.Initialize({ token: token });
                    Paddle.Checkout.open({
                        transactionId: transactionId,
                        settings: {
                            // Matches the app's own mode. Paddle defaults to light whatever it is
                            // sitting on, which on the ink ground is unreadable.
                            theme: dark ? 'dark' : 'light',
                            displayMode: 'inline',
                            frameTarget: 'paddle-checkout-frame',
                            frameInitialHeight: 480,
                            frameStyle: 'width:100%; min-width:312px; background-color: transparent; border: none;',
                            // Built in JS rather than interpolated from Kotlin: `js()` takes a
                            // compile-time constant and anything it names has to survive into the
                            // emitted, minified JS. The three parameters above already rely on that;
                            // a fresh local would be one more thing to be wrong about at runtime,
                            // on a page whose failure mode is a silent blank frame.
                            successUrl: window.location.origin + '/app/settings/billing/confirming'
                        }
                    });
                })()
                """,
            )
            Unit
        }
        if (existing != null) {
            run()
            continuation.resume(Unit) {}
            return@suspendCancellableCoroutine
        }
        val script = document.createElement("script")
        script.setAttribute("src", "https://cdn.paddle.com/paddle/v2/paddle.js")
        script.setAttribute("data-paddle", "true")
        script.addEventListener("load", {
            runCatching(run)
                .onSuccess { continuation.resume(Unit) {} }
                .onFailure { continuation.resumeWith(Result.failure(it)) }
        })
        script.addEventListener("error", {
            continuation.resumeWith(Result.failure(IllegalStateException("Paddle.js failed to load")))
        })
        document.head?.appendChild(script)
    }
}

private external class URLSearchParams(init: String) {
    fun get(name: String): String?
}
